/* ============================================================================
   glyphSets.ts — 6 jeux de glyphes ordonnés du plus clair (index 0)
   au plus dense (dernier index). L'algorithme image-to-ASCII map
   `Math.floor(L * (set.length - 1))` où L = luminance [0..1].
   ============================================================================ */

import type { GlyphSetId } from '../canvasStore';

/** Set classique 10-niveaux (Bourke court). */
export const CLASSIC = ' .,:-=+*#@';

/** Set dense 70-niveaux (Paul Bourke). */
export const DENSE =
  ' .\'^",:;Il!i><~+_-?][}{1)(|/tfjrxnuvczXYUJCLQ0OZmwqpdbkhao*#MW&8%B@$';

/** Set blocks Unicode minimal (5 niveaux). */
export const MINIMAL = ' ░▒▓█'; // ' ░▒▓█'

/** Set ornement / fairy (8 niveaux). */
export const FAIRY = ' ·*+✦✧❀❉'; // ' ·*+✦✧❀❉'

/** Set symbols (image fournie). */
export const SYMBOLS = ' .-:+*@%#';

/** Set blocks horizontal Unicode (9 niveaux). */
export const BLOCKS = ' ▁▂▃▄▅▆▇█'; // ' ▁▂▃▄▅▆▇█'

/**
 * Set cyanotype (10 niveaux) — glyphes circulaires gradués reproduisant
 * l'effet pointillisme d'un tirage cyanotype (procédé photo bleu) :
 * fines pointes de lumière variant en taille sur fond sombre.
 * Combiner avec : background `ink` + invert OFF, ou background `paper` + invert ON.
 */
export const CYANOTYPE = ' ·°∘∙◦○⊙◉●';

/**
 * Set halftone (10 niveaux) — pattern d'impression journal classique.
 * Comme cyanotype mais SANS jitter aléatoire : cercles alignés sur grille
 * régulière, radius proportionnel à la luminance. Esthétique TouchDesigner
 * / impression typographique 19e siècle.
 * Combiner avec : background `paper` + invert OFF (noir sur blanc).
 */
export const HALFTONE = ' ·°∘∙◦○⊙◉●';

/**
 * Set liquid (10 niveaux) — ASCII fluide animé. Inspiré Unseen Studio "2025
 * Wrapped" (scan CX 2026-05-12 TOP 10 #5) : texte/image qui se liquéfie en
 * caractères ASCII ondulants. Glyphes classiques mais rendus avec offset
 * wave continu via LiquidLayer (canvas 2D + RAF).
 */
export const LIQUID = ' .,:;io8%@';

export const GLYPH_SETS: Record<GlyphSetId, string> = {
  classic: CLASSIC,
  dense: DENSE,
  minimal: MINIMAL,
  fairy: FAIRY,
  symbols: SYMBOLS,
  blocks: BLOCKS,
  cyanotype: CYANOTYPE,
  halftone: HALFTONE,
  liquid: LIQUID,
};

/** Retourne le set, optionnellement inversé (chars sombres en haut). */
export function getGlyphSet(id: GlyphSetId, invert = false): string {
  const set = GLYPH_SETS[id] ?? CLASSIC;
  return invert ? set.split('').reverse().join('') : set;
}

/** Map luminance [0..1] → glyph. */
export function luminanceToGlyph(L: number, set: string): string {
  if (set.length === 0) return ' ';
  const clamped = Math.max(0, Math.min(1, L));
  const idx = Math.floor(clamped * (set.length - 1));
  return set.charAt(idx);
}
